import { ApiManager } from 'utlis/api_manager';
import http from 'utlis/http';

const api = new ApiManager(process.env.REACT_APP_BASE_URL, 1);

const productLibraryBase = workspaceUuid => `project/team/workspaces/${workspaceUuid}/product-library`;

/**
 * GET api/v1/project/team/workspaces/{workspace_uuid}/product-library/
 *
 * Returns product metadata, is_empty flag, and categories with flows.
 *
 * @param {string} workspaceUuid
 * @returns {{ product: object, is_empty: boolean, categories: Array }}
 */
export const getProductLibrary = async workspaceUuid => {
  const { data } = await api.setUrl(productLibraryBase(workspaceUuid)).get();

  return data;
};

/**
 * POST api/v1/project/team/workspaces/{workspace_uuid}/product-library/flows/
 *
 * Saves a new flow to the product library.
 *
 * @param {string} workspaceUuid
 * @param {{ name: string, source: string, category_name: string, assets: Array }} payload
 *   assets: [{ image_url, image_key?, thumbnail_url?, order }]
 * @returns {{ flow: object }}
 */
export const saveProductLibraryFlow = async (workspaceUuid, payload) => {
  const { data } = await api
    .setUrl(`${productLibraryBase(workspaceUuid)}/flows`)
    .post(payload);

  return data;
};

/**
 * GET api/v1/project/team/workspaces/{workspace_uuid}/product-library/flows/{flow_uuid}/
 *
 * Returns full details for a single flow. Pass versionNumber to fetch a specific version;
 * omit (or pass null/undefined) to get the latest version.
 *
 * @param {string} workspaceUuid
 * @param {string} flowUuid
 * @param {number} [versionNumber]
 * @returns {{ flow: object }}
 */
export const getFlowDetail = async (workspaceUuid, flowUuid, versionNumber) => {
  const params = versionNumber != null ? { version_number: versionNumber } : undefined;
  const { data } = await api
    .setUrl(`${productLibraryBase(workspaceUuid)}/flows/${flowUuid}`)
    .get(params);

  return data;
};

/**
 * POST api/v1/project/team/workspaces/{workspace_uuid}/product-library/flows/{flow_uuid}/versions/
 *
 * Adds a new version to an existing flow.
 *
 * @param {string} workspaceUuid
 * @param {string} flowUuid
 * @param {{ source: string, assets: Array<{ image_url: string, order: number }> }} payload
 * @returns {{ flow: object }}
 */
export const addFlowVersion = async (workspaceUuid, flowUuid, payload) => {
  const { data } = await api
    .setUrl(`${productLibraryBase(workspaceUuid)}/flows/${flowUuid}/versions`)
    .post(payload);

  return data;
};

/**
 * PATCH api/v1/project/team/workspaces/{workspace_uuid}/product-library/flows/{flow_uuid}/
 *
 * Renames a flow or reassigns it to a different category. Both fields are optional.
 *
 * @param {string} workspaceUuid
 * @param {string} flowUuid
 * @param {{ name?: string, category_name?: string }} payload
 * @returns {{ flow: object }}
 */
export const updateFlow = async (workspaceUuid, flowUuid, payload) => {
  const { data } = await api
    .setUrl(`${productLibraryBase(workspaceUuid)}/flows/${flowUuid}`)
    .patch(null, payload);

  return data;
};

/**
 * POST {REACT_APP_DESIGN_AUDIT_BASE_URL}/label_flow  (multipart/form-data)
 *
 * Classifies an ordered sequence of UI screenshot files into a (category, sub_flow) label.
 * Files are sent as frame_image_0, frame_image_1, … preserving the flow order.
 *
 * @param {File[]} imageFiles - Ordered array of screenshot File objects
 * @param {AbortSignal} [signal] - Optional abort signal to cancel the request
 * @returns {Promise<object>} Server response with category/sub_flow label
 */
export const classifyFlowImages = async (imageFiles, signal) => {
  const formData = new FormData();

  imageFiles.forEach((file, index) => {
    formData.append(`frame_image_${index}`, file);
  });

  const { data } = await http.post(
    `${process.env.REACT_APP_DESIGN_AUDIT_BASE_URL}/label_flow`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
        'X-BACKEND-URL': process.env.REACT_APP_X_BACKEND_URL,
      },
      signal,
    }
  );

  return data;
};
